import './a11y.css';
import React, { createContext, useContext, useEffect, useMemo, useReducer } from 'react';
import { DEFAULT_SETTINGS } from './a11y.types.js';
import { loadSettings, saveSettings } from './utils/persist.js';
import { applySettingsToDom, clearDomAttributes } from './utils/cssVars.js';

const A11yContext = createContext(null);

function reducer(state, action) {
  switch (action.type) {
    case 'SET':
      return { ...state, [action.key]: action.value };
    case 'TOGGLE':
      return { ...state, [action.key]: !state[action.key] };
    case 'CYCLE':
      return { ...state, [action.key]: action.next };
    case 'RESET':
      return { ...DEFAULT_SETTINGS, panelPosition: state.panelPosition };
    case 'HYDRATE':
      return { ...DEFAULT_SETTINGS, ...action.value };
    case 'MOVE_PANEL':
      return { ...state, panelPosition: { ...state.panelPosition, ...action.value } };
    default:
      return state;
  }
}

export function A11yProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, undefined, () => loadSettings());

  useEffect(() => {
    const id = setTimeout(() => saveSettings(state), 150);
    applySettingsToDom(state);
    return () => clearTimeout(id);
  }, [state]);

  useEffect(() => {
    return () => clearDomAttributes();
  }, []);

  const api = useMemo(
    () => ({
      settings: state,
      set: (key, value) => dispatch({ type: 'SET', key, value }),
      toggle: (key) => dispatch({ type: 'TOGGLE', key }),
      cycle: (key, next) => dispatch({ type: 'CYCLE', key, next }),
      reset: () => dispatch({ type: 'RESET' }),
      hydrate: (value) => dispatch({ type: 'HYDRATE', value }),
      movePanel: (pos) => dispatch({ type: 'MOVE_PANEL', value: pos }),
    }),
    [state]
  );

  return <A11yContext.Provider value={api}>{children}</A11yContext.Provider>;
}

export function useA11yContext() {
  const ctx = useContext(A11yContext);
  if (!ctx) throw new Error('useA11yContext must be used within A11yProvider');
  return ctx;
}
